import { DropzoneFile } from 'dropzone';
import Resumable from 'resumablejs';
import Api, {
    isErrorDueToUnauthorizedRequest,
    manageAuthorization
} from '../../../../../../shared/Api';
import OrderFilmApi, { OrderFilm, OrderFilms } from '../../OrderFilmApi';

type ResumableFile = DropzoneFile & {
    data: unknown;
};

export interface UploadHandler {
    upload: (file: File) => void;
    onProgress: (callback: (value: number) => void) => void;
    onError: (callback: (error: unknown) => void) => void;
    onSuccess: (callback: (films: OrderFilm[]) => void) => void;
}

export type UploadHandlerFactory = (orderId: number, orderRowId: number) => UploadHandler;

export function createUploadHandlerFactory() {
    let filmUploadApi = new Api();

    return function createUploadHandler(orderId: number, orderRowId: number): UploadHandler {
        let resumable = new Resumable({
            target: `${filmUploadApi.getServiceBase()}/views/orders/${orderId}/films/${orderRowId}/chunk`,
            headers: () => filmUploadApi.appendHeaders(),
            testChunks: false
        });

        resumable.on('fileAdded', () => {
            resumable.upload();
        });

        return {
            upload(file: ResumableFile) {
                let reader = new FileReader();
                reader.onloadend = () => {
                    file.data = reader.result;
                    resumable.addFile(file);
                };
                reader.readAsArrayBuffer(file);
            },
            onProgress(callback: (value: number) => void) {
                resumable.on('progress', () => {
                    let value = resumable.progress();
                    callback(value);
                });
            },
            onError(callback: (error: unknown) => void) {
                resumable.on('fileError', (file, message) => {
                    if (isErrorDueToUnauthorizedRequest(message)) {
                        manageAuthorization(file);
                    } else {
                        let error = message;
                        if (typeof message === 'string') {
                            try {
                                error = JSON.parse(message);
                            } catch (e) {}
                        }
                        resumable.cancel();
                        callback(error);
                    }
                });
            },
            onSuccess(callback: (films: OrderFilm[]) => void) {
                resumable.on('fileSuccess', async (result: any) => {
                    let response: OrderFilms = await OrderFilmApi.combineFilm(
                        orderId,
                        orderRowId,
                        result.fileName,
                        result.uniqueIdentifier,
                        result.chunks.length,
                        result.file.type
                    );

                    callback(response && response.films ? response.films : []);
                });
            }
        };
    };
}
